import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  SearchOutlined,
  CheckCircleOutline,
  ErrorOutline,
  RefreshOutlined,
  PauseCircleOutlined,
  SendOutlined,
  UploadFileOutlined,
  ExpandMore,
  ExpandLess,
  DescriptionOutlined,
  WarningAmberOutlined,
  InfoOutlined,
  ChatBubbleOutline,
  HistoryOutlined,
  PeopleOutline,
  CloudUploadOutlined,
} from "@mui/icons-material";
import {
  orange,
  green,
  red,
  blue,
  grey,
  amber,
  indigo,
  purple,
  teal,
  cyan,
} from "@mui/material/colors";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import size from "lodash/size";

import Txt from "../../components/typography/Txt";
import Flx from "../../components/layout/Flx";
import { useReggoraRouteHook } from "../../hooks/useReggoraRouteHook";
import {
  REGGORA_EVENT_DISPLAY,
  REGGORA_STATUS_COLORS,
  REGGORA_TERMINAL_STATUSES,
} from "../../constants/reggoraConstants";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { loanPipelineSet } from "../../redux/actions/loanPipelineActions";
import SelectAutoInput from "../../components/inputs/SelectAutoInput";
import InputWrapper from "../../components/inputs/shared/InputWrapper";
import ScreenContent from "../../components/layout/ScreenContent";
import Htag from "../../components/typography/Htag";
import LoanSearchDropdown from "../../components/inputs/common/LoanSearchDropdown";

// ─── Helpers ─────────────────────────────────────────────

const StatusChip = ({ status }) => {
  const colorMap = {
    warning: { bg: orange[50], color: orange[800], border: orange[200] },
    info: { bg: blue[50], color: blue[800], border: blue[200] },
    success: { bg: green[50], color: green[800], border: green[200] },
    error: { bg: red[50], color: red[800], border: red[200] },
    secondary: { bg: purple[50], color: purple[800], border: purple[200] },
    default: { bg: grey[100], color: grey[700], border: grey[300] },
  };

  const muiColor = REGGORA_STATUS_COLORS[status] || "default";
  const colors = colorMap[muiColor] || colorMap.default;

  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: colors.bg,
        color: colors.color,
        border: `1px solid ${colors.border}`,
        fontWeight: 600,
        fontSize: "12px",
      }}
    />
  );
};

const SectionPaper = ({ children, sx, ...props }) => (
  <Paper
    elevation={0}
    sx={{
      border: `1px solid ${grey[200]}`,
      borderRadius: "8px",
      p: 2.5,
      ...sx,
    }}
    {...props}
  >
    {children}
  </Paper>
);

// Event type → icon/color for the notifications feed
const EVENT_ICON_MAP = {
  order_created: { icon: "📋", color: green[100], textColor: green[800] },
  order_updated: { icon: "🔄", color: blue[100], textColor: blue[800] },
  status_update: { icon: "📊", color: indigo[100], textColor: indigo[800] },
  order_approved: { icon: "✅", color: green[100], textColor: green[800] },
  order_report_received: {
    icon: "📄",
    color: purple[100],
    textColor: purple[800],
  },
  order_ready_for_review: {
    icon: "👁",
    color: amber[100],
    textColor: amber[800],
  },
  order_report_sent: { icon: "📤", color: teal[100], textColor: teal[800] },
  consumer_payment_created: {
    icon: "💳",
    color: orange[100],
    textColor: orange[800],
  },
  consumer_payment_completed: {
    icon: "💰",
    color: green[100],
    textColor: green[800],
  },
  consumer_payment_updated: {
    icon: "💳",
    color: orange[100],
    textColor: orange[800],
  },
  consumer_payment_deleted: {
    icon: "❌",
    color: red[100],
    textColor: red[800],
  },
  hold: { icon: "⏸️", color: amber[100], textColor: amber[800] },
  conversation_message: {
    icon: "💬",
    color: blue[100],
    textColor: blue[800],
  },
};

// Avatar initials from a name string
const AvatarBadge = ({ name, bgColor, textColor }) => {
  const initials = name
    ? name
        .split(/\s+/)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
        fontWeight: 700,
        flexShrink: 0,
        backgroundColor: bgColor || grey[200],
        color: textColor || grey[700],
      }}
    >
      {initials}
    </Box>
  );
};

// ─── Step 1: Loan Lookup ─────────────────────────────────

const LoanLookupSection = ({ onDealFound }) => {
  const { loading, loanLookup, checkExistingLoans } = useReggoraRouteHook();
  const [loanNumber, setLoanNumber] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [error, setError] = useState(null);

  const handleLookup = useCallback(() => {
    if (isNil(loanNumber)) return;
    setError(null);
    setLookupResult(null);

    loanLookup({
      loanIdentifier: loanNumber,
      onSuccessFn: (data) => {
        if (data?.found === false) {
          setError("Loan not found. Please check the loan number.");
          return;
        }
        setLookupResult(data);

        checkExistingLoans({
          dealId: data.deal_id,
          onSuccessFn: (existingData) => {
            onDealFound({ ...data, existing: existingData });
          },
          onFailFn: () => {
            onDealFound({ ...data, existing: null });
          },
        });
      },
      onFailFn: (err) => {
        setError(err?.message || "Failed to look up loan.");
      },
    });
  }, [loanNumber, loanLookup, checkExistingLoans, onDealFound]);

  return (
    <SectionPaper>
      <Flx column gap={2}>
        <Txt bold sx={{ fontSize: "15px" }}>
          Find Loan
        </Txt>
        <Flx ac gap={1.5}>
          <InputWrapper label={"Find Loan"} sx={{ flexGrow: 1 }}>
            <Flx ac fw gap={1}>
              <LoanSearchDropdown
                value={loanNumber}
                onChange={(value) => setLoanNumber(value)}
              />
              <Button
                variant="contained"
                size="large"
                startIcon={
                  loading ? <CircularProgress size={16} /> : <SearchOutlined />
                }
                onClick={handleLookup}
                disabled={loading || !loanNumber}
              >
                Look Up
              </Button>
            </Flx>
          </InputWrapper>
        </Flx>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}

        {lookupResult && (
          <Alert
            severity="success"
            icon={<CheckCircleOutline />}
            sx={{ mt: 1 }}
          >
            <Txt bold>{lookupResult.loan_product_type}</Txt> —{" "}
            {lookupResult.loan_purpose}
            {lookupResult.borrower_names?.length > 0 && (
              <Txt body2 sx={{ mt: 0.5 }}>
                Borrower(s): {lookupResult.borrower_names.join(", ")}
              </Txt>
            )}
          </Alert>
        )}
      </Flx>
    </SectionPaper>
  );
};

// ─── Deal Summary ────────────────────────────────────────

const DealSummarySection = ({ deal }) => {
  if (!deal) return null;

  return (
    <SectionPaper>
      <Flx column gap={1.5}>
        <Flx ac jb>
          <Txt bold sx={{ fontSize: "15px" }}>
            Deal Summary
          </Txt>
          <Chip
            label={`#${deal.internal_loan_number || deal.sync_loan_id}`}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Flx>
        <Divider />
        <Flx gap={4} sx={{ flexWrap: "wrap" }}>
          <Flx column>
            <Txt body2 color="text.secondary">
              Product
            </Txt>
            <Txt bold>{deal.loan_product_type}</Txt>
          </Flx>
          <Flx column>
            <Txt body2 color="text.secondary">
              Purpose
            </Txt>
            <Txt bold>{deal.loan_purpose}</Txt>
          </Flx>
          <Flx column>
            <Txt body2 color="text.secondary">
              Entity
            </Txt>
            <Txt bold>{deal.borrower_entity || "—"}</Txt>
          </Flx>
        </Flx>

        {isArray(deal.properties) && deal.properties.length > 0 && (
          <>
            <Txt body2 bold sx={{ mt: 1 }}>
              Properties
            </Txt>
            {deal.properties.map((p, i) => (
              <Txt key={i} body2>
                {p.address}, {p.city}, {p.state} {p.zip}
              </Txt>
            ))}
          </>
        )}
      </Flx>
    </SectionPaper>
  );
};

// ─── Loan Edit Form (Step 3 review) ─────────────────────

const LoanEditSection = ({ loans, onLoansEdited }) => {
  const [editedLoans, setEditedLoans] = useState(loans || []);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    setEditedLoans(loans || []);
  }, [loans]);

  const updateLoanField = (loanIndex, fieldPath, value) => {
    setEditedLoans((prev) => {
      const updated = [...prev];
      const loan = { ...updated[loanIndex] };
      const keys = fieldPath.split(".");
      let target = loan;
      for (let i = 0; i < keys.length - 1; i++) {
        target[keys[i]] = { ...target[keys[i]] };
        target = target[keys[i]];
      }
      target[keys[keys.length - 1]] = value;
      updated[loanIndex] = loan;
      return updated;
    });
  };

  const currentLoan = editedLoans[activeTab];
  const payload = currentLoan?.loan_payload || {};
  const property = payload?.subject_property || {};
  const borrower = currentLoan?.primary_borrower || {};

  return (
    <SectionPaper>
      <Flx column gap={2}>
        <Flx ac jb>
          <Txt bold sx={{ fontSize: "15px" }}>
            Review Loan Data
          </Txt>
          <Button
            size="small"
            variant="contained"
            onClick={() => onLoansEdited(editedLoans)}
          >
            Confirm & Continue
          </Button>
        </Flx>

        {editedLoans.length > 1 && (
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            size="small"
          >
            {editedLoans.map((l, i) => (
              <Tab key={i} label={`Property ${i + 1}`} />
            ))}
          </Tabs>
        )}

        {currentLoan && (
          <Flx column gap={2}>
            <Txt body2 bold color="text.secondary">
              Loan Details
            </Txt>
            <Flx gap={2} sx={{ flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="Appraisal Type"
                value={payload.appraisal_type || ""}
                onChange={(e) =>
                  updateLoanField(
                    activeTab,
                    "loan_payload.appraisal_type",
                    e.target.value,
                  )
                }
              />
              <TextField
                size="small"
                label="Loan Amount"
                type="number"
                value={payload.loan_amount || ""}
                onChange={(e) =>
                  updateLoanField(
                    activeTab,
                    "loan_payload.loan_amount",
                    Number(e.target.value),
                  )
                }
              />
              <TextField
                size="small"
                label="Estimated Value"
                type="number"
                value={payload.estimated_value || ""}
                onChange={(e) =>
                  updateLoanField(
                    activeTab,
                    "loan_payload.estimated_value",
                    Number(e.target.value),
                  )
                }
              />
            </Flx>

            <Txt body2 bold color="text.secondary">
              Subject Property
            </Txt>
            <Flx gap={2} sx={{ flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="Address"
                value={property.address || ""}
                sx={{ minWidth: 200 }}
                onChange={(e) =>
                  updateLoanField(
                    activeTab,
                    "loan_payload.subject_property.address",
                    e.target.value,
                  )
                }
              />
              <TextField
                size="small"
                label="City"
                value={property.city || ""}
                onChange={(e) =>
                  updateLoanField(
                    activeTab,
                    "loan_payload.subject_property.city",
                    e.target.value,
                  )
                }
              />
              <TextField
                size="small"
                label="State"
                value={property.state || ""}
                sx={{ width: 80 }}
                onChange={(e) =>
                  updateLoanField(
                    activeTab,
                    "loan_payload.subject_property.state",
                    e.target.value,
                  )
                }
              />
              <TextField
                size="small"
                label="Zip"
                value={property.zip || ""}
                sx={{ width: 100 }}
                onChange={(e) =>
                  updateLoanField(
                    activeTab,
                    "loan_payload.subject_property.zip",
                    e.target.value,
                  )
                }
              />
            </Flx>

            <Txt body2 bold color="text.secondary">
              Primary Borrower
            </Txt>
            <Flx gap={2} sx={{ flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="First Name"
                value={borrower.first_name || ""}
                onChange={(e) =>
                  updateLoanField(
                    activeTab,
                    "primary_borrower.first_name",
                    e.target.value,
                  )
                }
              />
              <TextField
                size="small"
                label="Last Name"
                value={borrower.last_name || ""}
                onChange={(e) =>
                  updateLoanField(
                    activeTab,
                    "primary_borrower.last_name",
                    e.target.value,
                  )
                }
              />
              <TextField
                size="small"
                label="Email"
                value={borrower.email || ""}
                sx={{ minWidth: 200 }}
                onChange={(e) =>
                  updateLoanField(
                    activeTab,
                    "primary_borrower.email",
                    e.target.value,
                  )
                }
              />
              <TextField
                size="small"
                label="Phone"
                value={borrower.phone || ""}
                onChange={(e) =>
                  updateLoanField(
                    activeTab,
                    "primary_borrower.phone",
                    e.target.value,
                  )
                }
              />
            </Flx>
          </Flx>
        )}
      </Flx>
    </SectionPaper>
  );
};

// ─── Product Picker (Step 4) ─────────────────────────────

const ProductPickerSection = ({ onProductsSelected }) => {
  const { loading, fetchProducts } = useReggoraRouteHook();
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("Normal");

  useEffect(() => {
    fetchProducts({
      onSuccessFn: (data) => setProducts(data?.products || []),
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleProduct = (productId) => {
    setSelected((prev) =>
      prev.includes(productId)
        ? prev.filter((p) => p !== productId)
        : [...prev, productId],
    );
  };

  return (
    <SectionPaper>
      <Flx column gap={2}>
        <Txt bold sx={{ fontSize: "15px" }}>
          Select Appraisal Products
        </Txt>

        {loading && <CircularProgress size={20} />}

        {products.map((p) => (
          <Flx key={p.id} ac gap={1}>
            <Checkbox
              size="small"
              checked={selected.includes(p.id)}
              onChange={() => toggleProduct(p.id)}
            />
            <Txt body2>{p.name}</Txt>
          </Flx>
        ))}

        <Divider sx={{ my: 1 }} />

        <Flx gap={2} ac>
          <TextField
            size="small"
            type="date"
            label="Due Date"
            InputLabelProps={{ shrink: true }}
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
          <TextField
            size="small"
            select
            label="Priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ minWidth: 120 }}
          >
            <option value="Normal">Normal</option>
            <option value="Rush">Rush</option>
          </TextField>
        </Flx>

        <Button
          variant="contained"
          disabled={selected.length === 0}
          onClick={() =>
            onProductsSelected({ products: selected, dueDate, priority })
          }
        >
          Submit Orders
        </Button>
      </Flx>
    </SectionPaper>
  );
};

// ─── SSE Progress (Step 6) ───────────────────────────────

const OrderProgressSection = ({ progress }) => {
  if (!progress) return null;

  return (
    <SectionPaper>
      <Flx column gap={1}>
        <Flx ac jb>
          <Txt bold>{progress.message || "Processing..."}</Txt>
          <Txt body2 color="text.secondary">
            {progress.percent || 0}%
          </Txt>
        </Flx>
        <LinearProgress
          variant="determinate"
          value={progress.percent || 0}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Flx>
    </SectionPaper>
  );
};

// ══════════════════════════════════════════════════════════
// RIGHT COLUMN: Comments / Notifications Panel
// ══════════════════════════════════════════════════════════

const NotificationCommentItem = ({ notification, contacts }) => {
  const evtConfig = EVENT_ICON_MAP[notification.event_type] || {
    icon: "📌",
    color: grey[100],
    textColor: grey[700],
  };
  const eventDisplay = REGGORA_EVENT_DISPLAY[notification.event_type] || {
    label: notification.event_type,
  };

  const timestamp = notification.created_at || notification.event_time || "";
  const timeStr = timestamp ? new Date(timestamp).toLocaleString() : "";
  const message = notification.message || "";

  // Build a contact lookup for @mention rendering
  const contactMap = useMemo(() => {
    const map = {};
    (contacts || []).forEach((c) => {
      if (c.emailAddress) map[c.emailAddress.toLowerCase()] = c;
    });
    return map;
  }, [contacts]);

  // Parse email mentions out of the message
  const renderedMessage = useMemo(() => {
    const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    const parts = message.split(emailRegex);
    return parts.map((part, i) => {
      if (emailRegex.test(part)) {
        emailRegex.lastIndex = 0;
        const contact = contactMap[part.toLowerCase()];
        const displayName = contact
          ? `${contact.firstName || ""} ${contact.lastName || ""}`.trim() ||
            part
          : part;
        return (
          <Chip
            key={i}
            label={`@${displayName}`}
            size="small"
            sx={{
              height: 20,
              fontSize: "11px",
              fontWeight: 600,
              backgroundColor: blue[50],
              color: blue[700],
              mx: 0.25,
            }}
          />
        );
      }
      return <span key={i}>{part}</span>;
    });
  }, [message, contactMap]);

  const emailPayload = notification.email_payload;

  return (
    <Flx
      gap={1.5}
      sx={{
        py: 1.5,
        px: 1.5,
        "&:hover": { backgroundColor: grey[50] },
        borderRadius: 1,
        transition: "background-color 0.15s",
      }}
    >
      <AvatarBadge
        name={eventDisplay.label}
        bgColor={evtConfig.color}
        textColor={evtConfig.textColor}
      />
      <Flx column sx={{ flex: 1, minWidth: 0 }}>
        <Flx ac gap={1} sx={{ mb: 0.25 }}>
          <Txt
            body2
            bold
            sx={{
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <span>{evtConfig.icon}</span> {eventDisplay.label}
          </Txt>
          <Txt caption color="text.secondary">
            {timeStr}
          </Txt>
        </Flx>
        <Txt
          body2
          color="text.secondary"
          sx={{ fontSize: "13px", lineHeight: 1.5 }}
        >
          {renderedMessage}
        </Txt>

        {notification.event_data?.status && (
          <Box sx={{ mt: 0.5 }}>
            <StatusChip status={notification.event_data.status} />
          </Box>
        )}

        {emailPayload && (
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              backgroundColor: blue[50],
              border: `1px solid ${blue[200]}`,
              borderRadius: 1,
            }}
          >
            <Txt caption bold sx={{ color: blue[700] }}>
              Email queued
            </Txt>
            <Txt caption sx={{ color: blue[600] }}>
              To: {emailPayload.to}
            </Txt>
            <Txt caption sx={{ color: blue[600] }}>
              Subject: {emailPayload.subject}
            </Txt>
            {emailPayload.attachments?.length > 0 && (
              <Txt caption sx={{ color: blue[500], mt: 0.5 }}>
                📎 {emailPayload.attachments.length} attachment(s)
              </Txt>
            )}
          </Box>
        )}
      </Flx>
    </Flx>
  );
};

const CommentsPanel = ({ notifications, contacts }) => {
  if (!notifications || notifications.length === 0) {
    return (
      <Flx ac jc sx={{ py: 6 }}>
        <Txt body2 color="text.secondary" sx={{ fontStyle: "italic" }}>
          No notifications yet.
        </Txt>
      </Flx>
    );
  }

  const sorted = [...notifications].sort(
    (a, b) =>
      new Date(a.created_at || a.event_time || 0) -
      new Date(b.created_at || b.event_time || 0),
  );

  let lastDateLabel = "";

  return (
    <Flx column>
      {sorted.map((notif, i) => {
        const time = notif.created_at || notif.event_time || "";
        const dateLabel = time
          ? new Date(time).toLocaleDateString("en-US", {
              weekday: "long",
              month: "numeric",
              day: "numeric",
              year: "numeric",
            })
          : "";
        const showDateSep = dateLabel && dateLabel !== lastDateLabel;
        if (dateLabel) lastDateLabel = dateLabel;

        return (
          <React.Fragment key={i}>
            {showDateSep && (
              <Flx ac gap={1} sx={{ py: 1, px: 2 }}>
                <Divider sx={{ flex: 1 }} />
                <Txt
                  caption
                  color="text.secondary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {dateLabel}
                </Txt>
                <Divider sx={{ flex: 1 }} />
              </Flx>
            )}
            <NotificationCommentItem notification={notif} contacts={contacts} />
            {i < sorted.length - 1 && <Divider sx={{ mx: 1.5 }} />}
          </React.Fragment>
        );
      })}
    </Flx>
  );
};

// ─── Webhook History Panel ───────────────────────────────

const WebhookHistoryPanel = ({ events }) => {
  const [expandedIdx, setExpandedIdx] = useState(null);

  if (!events || events.length === 0) {
    return (
      <Flx ac jc sx={{ py: 6 }}>
        <Txt body2 color="text.secondary" sx={{ fontStyle: "italic" }}>
          No webhook events recorded yet.
        </Txt>
      </Flx>
    );
  }

  return (
    <Flx column>
      {events.map((evt, i) => {
        const evtConfig = EVENT_ICON_MAP[evt.event_type] || {
          icon: "📌",
          color: grey[100],
          textColor: grey[700],
        };
        const eventDisplay = REGGORA_EVENT_DISPLAY[evt.event_type] || {
          label: evt.event_type,
        };
        const time = evt.stored_at || evt.event_time || "";
        const timeStr = time ? new Date(time).toLocaleString() : "";

        return (
          <Box key={i} sx={{ py: 1, px: 1.5 }}>
            <Flx ac gap={1}>
              <Chip
                label={`${evtConfig.icon} ${eventDisplay.label}`}
                size="small"
                sx={{
                  backgroundColor: evtConfig.color,
                  color: evtConfig.textColor,
                  fontWeight: 600,
                  fontSize: "11px",
                }}
              />
              <Txt
                caption
                color="text.secondary"
                sx={{ fontFamily: "monospace", fontSize: "11px" }}
              >
                {evt.reggora_order_id || "—"}
              </Txt>
              <Txt caption color="text.secondary" sx={{ ml: "auto" }}>
                {timeStr}
              </Txt>
            </Flx>
            {evt.event_data && (
              <>
                <Txt
                  caption
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    cursor: "pointer",
                    "&:hover": { color: "text.primary" },
                  }}
                  onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                >
                  {expandedIdx === i ? "▼" : "▶"} Raw payload
                </Txt>
                <Collapse in={expandedIdx === i}>
                  <Box
                    component="pre"
                    sx={{
                      mt: 0.5,
                      p: 1,
                      backgroundColor: grey[50],
                      borderRadius: 1,
                      fontSize: "11px",
                      fontFamily: "monospace",
                      color: grey[600],
                      maxHeight: 150,
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {JSON.stringify(evt.event_data, null, 2)}
                  </Box>
                </Collapse>
              </>
            )}
            {i < events.length - 1 && <Divider sx={{ mt: 1 }} />}
          </Box>
        );
      })}
    </Flx>
  );
};

// ─── Right Column: Comments + History + Contacts ─────────

const CommentsHistoryColumn = ({
  notifications,
  webhookEvents,
  contacts,
  onRefresh,
  loading,
}) => {
  const [activeTab, setActiveTab] = useState("comments");

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${grey[200]}`,
        borderRadius: "8px",
        overflow: "hidden",
        position: "sticky",
        top: 16,
      }}
    >
      {/* Tab header */}
      <Box
        sx={{
          borderBottom: `1px solid ${grey[200]}`,
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Flx ac gap={0.5}>
          <Button
            size="small"
            variant={activeTab === "comments" ? "contained" : "text"}
            color={activeTab === "comments" ? "primary" : "inherit"}
            startIcon={
              <ChatBubbleOutline sx={{ fontSize: "14px !important" }} />
            }
            onClick={() => setActiveTab("comments")}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              minHeight: 30,
              px: 1.5,
              ...(activeTab !== "comments" && { color: grey[600] }),
            }}
          >
            Comments
            {notifications.length > 0 && (
              <Chip
                label={notifications.length}
                size="small"
                sx={{
                  ml: 0.5,
                  height: 18,
                  fontSize: "10px",
                  fontWeight: 700,
                  ...(activeTab === "comments"
                    ? {
                        backgroundColor: "rgba(255,255,255,0.3)",
                        color: "inherit",
                      }
                    : { backgroundColor: blue[600], color: "#fff" }),
                }}
              />
            )}
          </Button>
          <Button
            size="small"
            variant={activeTab === "history" ? "contained" : "text"}
            color={activeTab === "history" ? "primary" : "inherit"}
            startIcon={<HistoryOutlined sx={{ fontSize: "14px !important" }} />}
            onClick={() => setActiveTab("history")}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              minHeight: 30,
              px: 1.5,
              ...(activeTab !== "history" && { color: grey[600] }),
            }}
          >
            Webhooks
            {webhookEvents.length > 0 && (
              <Chip
                label={webhookEvents.length}
                size="small"
                sx={{
                  ml: 0.5,
                  height: 18,
                  fontSize: "10px",
                  fontWeight: 700,
                  backgroundColor: grey[500],
                  color: "#fff",
                }}
              />
            )}
          </Button>
        </Flx>
        <IconButton
          size="small"
          onClick={onRefresh}
          disabled={loading}
          sx={{ color: grey[400] }}
        >
          {loading ? (
            <CircularProgress size={14} />
          ) : (
            <RefreshOutlined sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Box>

      {/* Panel body */}
      <Box sx={{ maxHeight: 600, overflowY: "auto" }}>
        {activeTab === "comments" && (
          <CommentsPanel notifications={notifications} contacts={contacts} />
        )}
        {activeTab === "history" && (
          <WebhookHistoryPanel events={webhookEvents} />
        )}
      </Box>

      {/* Contacts footer */}
      {contacts?.length > 0 && (
        <Box sx={{ borderTop: `1px solid ${grey[200]}`, px: 2, py: 1.5 }}>
          <Flx ac gap={0.5} sx={{ mb: 1 }}>
            <PeopleOutline sx={{ fontSize: 14, color: grey[500] }} />
            <Txt caption color="text.secondary" bold>
              Deal Contacts ({contacts.length})
            </Txt>
          </Flx>
          <Flx sx={{ flexWrap: "wrap", gap: 0.5 }}>
            {contacts.map((c, ci) => {
              const name =
                [c.firstName, c.lastName].filter(Boolean).join(" ") ||
                c.emailAddress ||
                "?";
              const role = c.contact_type || c.role || "";
              return (
                <Chip
                  key={ci}
                  size="small"
                  label={`${name}${role ? ` (${role})` : ""}`}
                  sx={{
                    fontSize: "11px",
                    height: 24,
                    backgroundColor: grey[100],
                    color: grey[700],
                  }}
                />
              );
            })}
          </Flx>
        </Box>
      )}
    </Paper>
  );
};

// ══════════════════════════════════════════════════════════
// LEFT COLUMN: Individual Order Card
// ══════════════════════════════════════════════════════════

const OrderCard = ({ order, dealId, onRefresh }) => {
  const {
    fetchConversation,
    sendConversationMessage,
    holdOrder,
    fetchDealDocuments,
    uploadEvaultDocs,
    loading,
  } = useReggoraRouteHook();

  const [expanded, setExpanded] = useState(false);

  // Conversation state
  const [conversation, setConversation] = useState(null);
  const [convLoading, setConvLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [showConversation, setShowConversation] = useState(false);

  // Hold state
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [holdReason, setHoldReason] = useState("");

  // eVault state
  const [showEvault, setShowEvault] = useState(false);
  const [evaultDocs, setEvaultDocs] = useState(null);
  const [evaultLoading, setEvaultLoading] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [evaultResult, setEvaultResult] = useState(null);

  const isTerminal = REGGORA_TERMINAL_STATUSES.includes(order.status);
  const notifications = order.notifications || [];
  const orderId = order.external_order_id || order.reggora_order_id || "";

  // ─── Conversation ──────────────────────────────────
  const loadConversation = useCallback(() => {
    setConvLoading(true);
    fetchConversation({
      orderId,
      onSuccessFn: (data) => {
        setConversation(data?.conversation || data);
        setConvLoading(false);
      },
      onFailFn: () => setConvLoading(false),
    });
  }, [orderId, fetchConversation]);

  const handleToggleConversation = useCallback(() => {
    const opening = !showConversation;
    setShowConversation(opening);
    if (opening) loadConversation();
  }, [showConversation, loadConversation]);

  const handleSendMessage = useCallback(() => {
    if (!newMessage.trim()) return;
    sendConversationMessage({
      orderId,
      message: newMessage.trim(),
      onSuccessFn: () => {
        setNewMessage("");
        loadConversation();
        if (onRefresh) onRefresh();
      },
    });
  }, [
    newMessage,
    orderId,
    sendConversationMessage,
    loadConversation,
    onRefresh,
  ]);

  // ─── Hold ──────────────────────────────────────────
  const handleHold = useCallback(() => {
    holdOrder({
      reggoraOrderId: orderId,
      reason: holdReason,
      onSuccessFn: () => {
        setHoldDialogOpen(false);
        setHoldReason("");
        if (onRefresh) onRefresh();
      },
    });
  }, [holdReason, orderId, holdOrder, onRefresh]);

  // ─── eVault ────────────────────────────────────────
  const handleToggleEvault = useCallback(() => {
    const opening = !showEvault;
    setShowEvault(opening);
    if (opening && !evaultDocs) {
      setEvaultLoading(true);
      fetchDealDocuments({
        dealId,
        onSuccessFn: (data) => {
          setEvaultDocs(data);
          setEvaultLoading(false);
        },
        onFailFn: () => setEvaultLoading(false),
      });
    }
  }, [showEvault, evaultDocs, dealId, fetchDealDocuments]);

  const handleEvaultUpload = useCallback(() => {
    if (selectedDocs.length === 0) return;
    uploadEvaultDocs({
      reggoraOrderId: orderId,
      dealId,
      docKeys: selectedDocs,
      onSuccessFn: (result) => {
        setEvaultResult(result);
        setSelectedDocs([]);
      },
    });
  }, [selectedDocs, orderId, dealId, uploadEvaultDocs]);

  // Collect messages from conversation
  const conversationMessages = useMemo(() => {
    if (!conversation) return [];
    const conv = conversation.conversation || conversation;
    return conv?.messages || conv?.data?.messages || [];
  }, [conversation]);

  return (
    <SectionPaper sx={{ mb: 2 }}>
      {/* ── Header ─────────────────────────────────────── */}
      <Flx ac jb>
        <Flx ac gap={1.5}>
          <Flx column>
            <Flx ac gap={1}>
              <Txt bold>Property {(order.property_index ?? 0) + 1}</Txt>
              <StatusChip status={order.status || "Unknown"} />
            </Flx>
            <Txt
              caption
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              {orderId}
            </Txt>
          </Flx>
        </Flx>

        <Flx ac gap={0.5}>
          {!isTerminal && (
            <>
              <Tooltip title="Send Message">
                <IconButton size="small" onClick={handleToggleConversation}>
                  <ChatBubbleOutline
                    fontSize="small"
                    sx={{
                      color: showConversation ? blue[600] : grey[500],
                    }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Place on Hold">
                <IconButton
                  size="small"
                  onClick={() => setHoldDialogOpen(true)}
                >
                  <PauseCircleOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Upload to eVault">
                <IconButton size="small" onClick={handleToggleEvault}>
                  <CloudUploadOutlined
                    fontSize="small"
                    sx={{ color: showEvault ? blue[600] : grey[500] }}
                  />
                </IconButton>
              </Tooltip>
            </>
          )}
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Flx>
      </Flx>

      {/* Inspection info */}
      {order.inspection_date && (
        <Txt body2 sx={{ mt: 1 }} color="text.secondary">
          Inspection: {new Date(order.inspection_date).toLocaleDateString()}
          {order.inspection_complete ? " ✓ Complete" : ""}
        </Txt>
      )}

      {/* Payment info */}
      {order.payment_completed && (
        <Txt body2 sx={{ mt: 0.5 }} color="success.main">
          Payment completed
        </Txt>
      )}

      {/* Inline notifications summary (collapsed) */}
      {!expanded && notifications.length > 0 && (
        <Flx ac gap={0.5} sx={{ mt: 1.5 }}>
          <Txt caption color="text.secondary">
            {notifications.length} notification
            {notifications.length !== 1 ? "s" : ""}
          </Txt>
          <Txt caption color="text.secondary">
            · Last:{" "}
            {
              (
                REGGORA_EVENT_DISPLAY[
                  notifications[notifications.length - 1]?.event_type
                ] || { label: "update" }
              ).label
            }
          </Txt>
        </Flx>
      )}

      {/* ── Expanded: full notification list ────────────── */}
      <Collapse in={expanded}>
        {notifications.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Txt body2 bold sx={{ mb: 1 }}>
              Notifications ({notifications.length})
            </Txt>
            <Box
              sx={{
                maxHeight: 200,
                overflowY: "auto",
                border: `1px solid ${grey[100]}`,
                borderRadius: 1,
              }}
            >
              {notifications.map((n, i) => {
                const evtConfig = EVENT_ICON_MAP[n.event_type] || {
                  icon: "📌",
                };
                const evtDisplay = REGGORA_EVENT_DISPLAY[n.event_type] || {
                  label: n.event_type,
                };
                const ts = n.created_at
                  ? new Date(n.created_at).toLocaleString()
                  : "";
                return (
                  <React.Fragment key={i}>
                    <Flx gap={1} sx={{ py: 1, px: 1.5 }}>
                      <Txt caption sx={{ flexShrink: 0 }}>
                        {evtConfig.icon}
                      </Txt>
                      <Flx column sx={{ flex: 1 }}>
                        <Flx ac jb>
                          <Txt caption bold>
                            {evtDisplay.label}
                          </Txt>
                          <Txt
                            caption
                            color="text.secondary"
                            sx={{ fontSize: "11px" }}
                          >
                            {ts}
                          </Txt>
                        </Flx>
                        <Txt
                          caption
                          color="text.secondary"
                          sx={{ fontSize: "12px" }}
                        >
                          {n.message}
                        </Txt>
                      </Flx>
                    </Flx>
                    {i < notifications.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        )}
      </Collapse>

      {/* ── Conversation panel ─────────────────────────── */}
      <Collapse in={showConversation}>
        <Divider sx={{ my: 2 }} />
        <Flx column gap={1.5}>
          <Flx ac jb>
            <Txt body2 bold>
              Conversation
            </Txt>
            {conversation?.conversation_id && (
              <Txt
                caption
                color="text.secondary"
                sx={{ fontFamily: "monospace" }}
              >
                {conversation.conversation_id}
              </Txt>
            )}
          </Flx>

          {convLoading ? (
            <Flx ac gap={1} sx={{ py: 2 }}>
              <CircularProgress size={14} />
              <Txt caption color="text.secondary">
                Loading conversation...
              </Txt>
            </Flx>
          ) : conversation?.error ? (
            <Txt caption color="error.main">
              {conversation.error}
            </Txt>
          ) : conversation?.note ? (
            <Txt caption color="text.secondary" sx={{ fontStyle: "italic" }}>
              {conversation.note}
            </Txt>
          ) : conversationMessages.length > 0 ? (
            <Box
              sx={{
                maxHeight: 200,
                overflowY: "auto",
                p: 1,
                backgroundColor: grey[50],
                borderRadius: 1,
                border: `1px solid ${grey[200]}`,
              }}
            >
              {conversationMessages.map((msg, i) => (
                <Flx key={i} column sx={{ mb: 1 }}>
                  <Flx ac jb>
                    <Txt caption bold sx={{ color: blue[700] }}>
                      {msg.sender_name || msg.sender || msg.from || "Unknown"}
                    </Txt>
                    <Txt
                      caption
                      color="text.secondary"
                      sx={{ fontSize: "11px" }}
                    >
                      {(msg.created || msg.created_at) &&
                        new Date(
                          msg.created || msg.created_at,
                        ).toLocaleString()}
                    </Txt>
                  </Flx>
                  <Txt body2 sx={{ fontSize: "13px" }}>
                    {msg.message || msg.text || msg.body || ""}
                  </Txt>
                </Flx>
              ))}
            </Box>
          ) : (
            <Txt body2 color="text.secondary" sx={{ fontStyle: "italic" }}>
              No messages yet.
            </Txt>
          )}

          {/* Send message input */}
          <Flx ac gap={1}>
            <TextField
              size="small"
              fullWidth
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
              multiline
              maxRows={3}
            />
            <IconButton
              size="small"
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
              sx={{ color: blue[600] }}
            >
              <SendOutlined />
            </IconButton>
          </Flx>
        </Flx>
      </Collapse>

      {/* ── eVault upload panel ────────────────────────── */}
      <Collapse in={showEvault}>
        <Divider sx={{ my: 2 }} />
        <Flx column gap={1.5}>
          <Txt body2 bold>
            Upload to eVault
          </Txt>

          {evaultLoading ? (
            <Flx ac gap={1} sx={{ py: 2 }}>
              <CircularProgress size={14} />
              <Txt caption color="text.secondary">
                Loading documents...
              </Txt>
            </Flx>
          ) : !evaultDocs ? (
            <Txt caption color="text.secondary">
              No document data loaded.
            </Txt>
          ) : (
            <>
              {(evaultDocs.documents || []).length === 0 &&
              (evaultDocs.blobFiles || []).length === 0 ? (
                <Txt
                  caption
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  No documents found for this deal.
                </Txt>
              ) : (
                <Box
                  sx={{
                    maxHeight: 250,
                    overflowY: "auto",
                    border: `1px solid ${grey[200]}`,
                    borderRadius: 1,
                  }}
                >
                  {(evaultDocs.documents || [])
                    .filter((d) => d.blobFileKey)
                    .map((doc, i) => (
                      <Flx
                        key={`doc-${i}`}
                        ac
                        gap={1}
                        sx={{
                          px: 1.5,
                          py: 1,
                          "&:hover": { backgroundColor: grey[50] },
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setSelectedDocs((prev) =>
                            prev.includes(doc.blobFileKey)
                              ? prev.filter((k) => k !== doc.blobFileKey)
                              : [...prev, doc.blobFileKey],
                          );
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedDocs.includes(doc.blobFileKey)}
                          sx={{ p: 0.25 }}
                        />
                        <DescriptionOutlined
                          sx={{ fontSize: 16, color: grey[500] }}
                        />
                        <Txt body2 sx={{ flex: 1, fontSize: "13px" }}>
                          {doc.originalFileName || doc.blobFileKey}
                        </Txt>
                        {doc.docGroup && (
                          <Chip
                            label={doc.docGroup}
                            size="small"
                            sx={{ fontSize: "10px", height: 20 }}
                          />
                        )}
                        {doc.docType && (
                          <Chip
                            label={doc.docType}
                            size="small"
                            sx={{
                              fontSize: "10px",
                              height: 20,
                              backgroundColor: blue[50],
                              color: blue[700],
                            }}
                          />
                        )}
                      </Flx>
                    ))}

                  {/* Blob files not already in documents */}
                  {(evaultDocs.blobFiles || [])
                    .filter(
                      (bf) =>
                        !(evaultDocs.documents || []).some(
                          (d) => d.blobFileKey === bf.name,
                        ),
                    )
                    .map((bf, i) => (
                      <Flx
                        key={`blob-${i}`}
                        ac
                        gap={1}
                        sx={{
                          px: 1.5,
                          py: 1,
                          "&:hover": { backgroundColor: grey[50] },
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setSelectedDocs((prev) =>
                            prev.includes(bf.name)
                              ? prev.filter((k) => k !== bf.name)
                              : [...prev, bf.name],
                          );
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedDocs.includes(bf.name)}
                          sx={{ p: 0.25 }}
                        />
                        <DescriptionOutlined
                          sx={{ fontSize: 16, color: grey[400] }}
                        />
                        <Txt body2 sx={{ flex: 1, fontSize: "13px" }}>
                          {bf.name}
                        </Txt>
                        {bf.size && (
                          <Txt caption color="text.secondary">
                            {(bf.size / 1024).toFixed(1)} KB
                          </Txt>
                        )}
                      </Flx>
                    ))}
                </Box>
              )}

              {selectedDocs.length > 0 && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CloudUploadOutlined />}
                  onClick={handleEvaultUpload}
                  disabled={loading}
                >
                  Upload {selectedDocs.length} doc
                  {selectedDocs.length !== 1 ? "s" : ""} to eVault
                </Button>
              )}

              {evaultResult && (
                <Alert
                  severity={
                    evaultResult.errors?.length > 0 ? "warning" : "success"
                  }
                  sx={{ mt: 1 }}
                >
                  {evaultResult.uploaded?.length || 0} uploaded
                  {evaultResult.errors?.length > 0 && (
                    <Txt body2 sx={{ mt: 0.5 }}>
                      {evaultResult.errors.length} error(s):{" "}
                      {evaultResult.errors
                        .map((e) => `${e.doc_key}: ${e.error}`)
                        .join(", ")}
                    </Txt>
                  )}
                </Alert>
              )}
            </>
          )}
        </Flx>
      </Collapse>

      {/* ── Hold dialog ────────────────────────────────── */}
      <Dialog
        open={holdDialogOpen}
        onClose={() => setHoldDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Place Order on Hold</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Reason for placing the order on hold..."
            value={holdReason}
            onChange={(e) => setHoldReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHoldDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleHold}
            disabled={!holdReason.trim()}
          >
            Place on Hold
          </Button>
        </DialogActions>
      </Dialog>
    </SectionPaper>
  );
};

// ══════════════════════════════════════════════════════════
// POST-ORDER: Two-Column Order Management Panel
// ══════════════════════════════════════════════════════════

const OrderManagementPanel = ({ dealId }) => {
  const { loading, fetchOrders, fetchLoanDiff } = useReggoraRouteHook();
  const [ordersData, setOrdersData] = useState(null);
  const [diffData, setDiffData] = useState(null);

  const loadOrders = useCallback(() => {
    fetchOrders({
      dealId,
      onSuccessFn: (data) => setOrdersData(data),
    });
    fetchLoanDiff({
      dealId,
      onSuccessFn: (data) => setDiffData(data),
    });
  }, [dealId, fetchOrders, fetchLoanDiff]);

  useEffect(() => {
    if (dealId) loadOrders();
  }, [dealId]); // eslint-disable-line react-hooks/exhaustive-deps

  const orders = ordersData?.orders || [];
  const webhookEvents = ordersData?.webhook_events || [];
  const contacts = ordersData?.loanRelatedContacts || [];

  // Aggregate all notifications across all orders
  const allNotifications = useMemo(() => {
    return orders.flatMap((order) =>
      (order.notifications || []).map((n) => ({
        ...n,
        order_id: order.external_order_id || order.reggora_order_id || "",
      })),
    );
  }, [orders]);

  return (
    <Flx column gap={2}>
      {/* Header */}
      <Flx ac jb>
        <Txt bold sx={{ fontSize: "17px" }}>
          Appraisal Orders
        </Txt>
        <Button
          size="small"
          startIcon={
            loading ? <CircularProgress size={14} /> : <RefreshOutlined />
          }
          onClick={loadOrders}
        >
          Refresh
        </Button>
      </Flx>

      {/* Loan diff warning */}
      {diffData?.has_action_required && (
        <Alert
          severity="warning"
          icon={<WarningAmberOutlined />}
          sx={{ border: `1px solid ${amber[200]}` }}
        >
          <Flx column gap={0.5}>
            <Txt bold body2>
              Deal data has changed since orders were placed
            </Txt>
            {diffData.diffs?.map((diff, i) =>
              diff.changes?.map((change, j) => (
                <Txt key={`${i}-${j}`} body2>
                  • {change.detail}
                  {change.action_hint && (
                    <Txt body2 bold component="span" color="warning.main">
                      {" "}
                      — {change.action_hint}
                    </Txt>
                  )}
                </Txt>
              )),
            )}
          </Flx>
        </Alert>
      )}

      {/* ── Two-column layout ─────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" },
          gap: 3,
          alignItems: "start",
        }}
      >
        {/* LEFT: Order cards */}
        <Flx column gap={0}>
          {orders.length === 0 && !loading && (
            <Txt body2 color="text.secondary">
              No orders found for this deal.
            </Txt>
          )}

          {orders.map((order, i) => (
            <OrderCard
              key={order.external_order_id || i}
              order={order}
              dealId={dealId}
              onRefresh={loadOrders}
            />
          ))}
        </Flx>

        {/* RIGHT: Comments + Webhook History */}
        <CommentsHistoryColumn
          notifications={allNotifications}
          webhookEvents={webhookEvents}
          contacts={contacts}
          onRefresh={loadOrders}
          loading={loading}
        />
      </Box>
    </Flx>
  );
};

// ══════════════════════════════════════════════════════════
// Main Screen
// ══════════════════════════════════════════════════════════

const ReggoraAppraisalScreen = () => {
  const user = useSelector((state) => state.user);
  const { loading, sseProgress, createLoans, submitOrders } =
    useReggoraRouteHook();

  // Flow state
  const [deal, setDeal] = useState(null);
  const [step, setStep] = useState("lookup");
  const [loans, setLoans] = useState([]);
  const [editedLoans, setEditedLoans] = useState([]);
  const [orderProgress, setOrderProgress] = useState(null);

  const handleDealFound = useCallback(
    (data) => {
      setDeal(data);

      if (data.existing?.has_orders) {
        setStep("orders");
        return;
      }

      if (data.existing?.has_loans) {
        setLoans(data.existing.loans || []);
        setStep("products");
        return;
      }

      // Create loans
      createLoans({
        dealId: data.deal_id,
        userEmail: user?.emailAddress,
        onSuccessFn: (loansData) => {
          setLoans(loansData?.loans || []);
          setStep("review");
        },
        onFailFn: () => {
          // Stay on lookup with the deal visible
        },
      });
    },
    [createLoans, user?.emailAddress],
  );

  const handleLoansEdited = useCallback((edited) => {
    setEditedLoans(edited);
    setStep("products");
  }, []);

  const handleSubmitOrders = useCallback(
    ({ products: selectedProducts, dueDate, priority }) => {
      if (!deal?.deal_id) return;

      setStep("submitting");
      setOrderProgress(null);

      submitOrders({
        dealId: deal.deal_id,
        loans: editedLoans.length > 0 ? editedLoans : loans,
        selectedProducts,
        dueDate: dueDate || undefined,
        priority,
        userEmail: user?.emailAddress,
        onProgressFn: (progress) => setOrderProgress(progress),
        onSuccessFn: () => {
          setStep("orders");
          setOrderProgress(null);
        },
        onFailFn: () => {
          setStep("products");
          setOrderProgress(null);
        },
      });
    },
    [deal, editedLoans, loans, submitOrders, user?.emailAddress],
  );

  const handleReset = useCallback(() => {
    setDeal(null);
    setStep("lookup");
    setLoans([]);
    setEditedLoans([]);
    setOrderProgress(null);
  }, []);

  return (
    <Box
      sx={{
        background: "#f5f8fa",
        minHeight: "100vh",
        pb: 6,
      }}
    >
      <Box
        sx={{
          background: "#ffffff",
          pt: 3,
          pb: 3,
          boxShadow: "0 0 0px 1px #dde0e4",
        }}
      >
        <ScreenContent suppressPaddingTopBottom>
          <Flx column gap={3}>
            <Flx fw jb ac>
              <Flx ac gap={1}>
                <DescriptionOutlined className="thin" />
                <Htag h1>Order Appraisal</Htag>
              </Flx>
              {deal && (
                <Button size="small" variant="text" onClick={handleReset}>
                  Start Over
                </Button>
              )}
            </Flx>
          </Flx>
        </ScreenContent>
      </Box>

      {/* Content — wider max-width on the orders step to fit two columns */}
      <Box
        sx={{
          maxWidth: step === "orders" ? "1200px" : "md",
          mx: "auto",
          p: 3,
        }}
      >
        <Flx column gap={2.5}>
          {step === "lookup" && (
            <LoanLookupSection onDealFound={handleDealFound} />
          )}

          {deal && <DealSummarySection deal={deal} />}

          {step === "review" && loans.length > 0 && (
            <LoanEditSection loans={loans} onLoansEdited={handleLoansEdited} />
          )}

          {step === "products" && (
            <ProductPickerSection onProductsSelected={handleSubmitOrders} />
          )}

          {step === "submitting" && (
            <OrderProgressSection progress={orderProgress || sseProgress} />
          )}

          {step === "orders" && deal?.deal_id && (
            <OrderManagementPanel dealId={deal.deal_id} />
          )}
        </Flx>
      </Box>
    </Box>
  );
};

export default ReggoraAppraisalScreen;
